import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { match, RouteComponentProps } from 'react-router-dom';
import GoogleMapReact, { Coords } from 'google-map-react';
import { Button, Icon } from '@material-ui/core';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import { TooltipButton } from '../../../components/tooltip-button';

// Modules
import { actions as errorActions, ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as towersActions,
    selectors as towersSelectors,
    types as towersTypes,
} from '../../../redux/reducers/towers';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { Tower, TParams } from '../../../redux/reducers/api-types';

// Resources
import withGlobalStyles from '../../../utils/global-styles';
import MapUtils from '../../../utils/mapUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import styles from './towers-list-style';
import { paths } from '../../paths';
import Listing from '../../../components/listing';
import ButtonTo from '../../../components/buttonTo';
import MainLoading from '../../../components/main-loading';
import { CIRCLE_COLOR_DEFAULT, CIRCLE_COLOR_SELECTED } from '../../../utils/constants';
import envConfig from '../../../config';

interface ControlTowersListProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    classes: any;
    center: Coords;
    zoom: number;
    towers: Tower[];
    getTowersError: ErrorPayload;
    clearError: (errorType: string) => void;
    getTowers: () => void;
    isLoadingControlTowers: boolean;
}

interface ControlTowersListState {}

class ControlTowersList extends React.Component<ControlTowersListProps, ControlTowersListState> {
    map: any;
    maps: any;
    mapWrapper: React.RefObject<HTMLDivElement>;
    mapWrapperFixed: boolean;
    markers: any[];
    selectedMaker: any;

    isDriverPrebookWrite: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.W,
    });

    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: 5,
    };

    constructor(props) {
        super(props);
        this.mapWrapper = React.createRef();
        this.handleScroll = this.handleScroll.bind(this);
        this.markers = [];
        this.selectedMaker = null;
    }

    componentDidMount() {
        this.load();
    }

    load = (): void => {
        // A failure recorded for the entity we come from would otherwise greet the next
        // one with the retry panel instead of the loader.
        this.props.clearError(towersTypes.GET_TOWERS);

        if (!this.props.towers.length) {
            this.props.getTowers();
        }
        document.addEventListener('scroll', this.handleScroll);
    };

    componentWillUnmount() {
        document.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll() {
        const mapDiv = this.mapWrapper.current;
        if (mapDiv) {
            const minPos = 10;
            const mapDivPosition = mapDiv.getBoundingClientRect();
            if (!mapDivPosition.width) {
                return;
            }
            if (this.mapWrapperFixed) {
                const mapDivCtnPosition = (mapDiv.parentNode as HTMLDivElement).getBoundingClientRect();
                if (mapDivCtnPosition.y > mapDivPosition.y) {
                    mapDiv.classList.remove(this.props.classes.mapWrapperFixed);
                    mapDiv.style.top = '';
                    mapDiv.style.width = '';
                    mapDiv.style.height = '';
                    this.mapWrapperFixed = false;
                }
            } else {
                if (mapDivPosition.y <= minPos) {
                    mapDiv.classList.add(this.props.classes.mapWrapperFixed);
                    mapDiv.style.top = minPos + 'px';
                    mapDiv.style.width = mapDivPosition.width + 'px';
                    mapDiv.style.height = mapDivPosition.height + 'px';
                    this.mapWrapperFixed = true;
                }
            }
        }
    }

    handleApiLoaded = (map: any, maps: any) => {
        this.map = map;
        this.maps = maps;
        this.updateMarkers();
    };

    handleCenterMap = (tower: Tower) => () => {
        if (this.map && this.maps) {
            this.map.setCenter({ lat: tower.position.lat, lng: tower.position.lng });
            const bounds = new this.maps.LatLngBounds();
            const radiusKm = tower.radius / 1000;
            bounds.extend({ lat: tower.position.lat + radiusKm / 111, lng: tower.position.lng });
            bounds.extend({ lat: tower.position.lat - radiusKm / 111, lng: tower.position.lng });
            bounds.extend({
                lat: tower.position.lat,
                lng: tower.position.lng + radiusKm / (111 * Math.cos(tower.position.lat * (Math.PI / 180))),
            });
            bounds.extend({
                lat: tower.position.lat,
                lng: tower.position.lng - radiusKm / (111 * Math.cos(tower.position.lat * (Math.PI / 180))),
            });
            this.map.fitBounds(bounds);
            this.selectedMaker = tower;
            this.updateMarkers();
        }
    };

    updateSelectedMarker = () => {
        this.markers.forEach((marker) => {
            if (!this.selectedMaker && marker.strokeColor === CIRCLE_COLOR_SELECTED) {
                marker.setOptions({
                    strokeColor: CIRCLE_COLOR_DEFAULT,
                    fillColor: CIRCLE_COLOR_DEFAULT,
                    map: this.map,
                });
            }
            if (this.selectedMaker && marker.name === this.selectedMaker.name) {
                marker.setOptions({
                    strokeColor: CIRCLE_COLOR_SELECTED,
                    fillColor: CIRCLE_COLOR_SELECTED,
                    map: this.map,
                });
            }
        });
    };

    updateMarkers() {
        // Clear existing markers
        this.markers.forEach((marker) => marker.setMap(null));
        this.markers = [];

        // Add new markers
        const { towers } = this.props;
        if (towers && this.map && this.maps) {
            towers.forEach((tower) => {
                const marker = new this.maps.Marker({
                    position: { lat: tower.position.lat, lng: tower.position.lng },
                    map: this.map,
                    title: tower.name,
                });

                // Create a circle for the tower's radius
                const circleColor = CIRCLE_COLOR_DEFAULT;
                const circle = new this.maps.Circle({
                    strokeColor: circleColor,
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: circleColor,
                    fillOpacity: 0.15,
                    map: this.map,
                    center: { lat: tower.position.lat, lng: tower.position.lng },
                    radius: tower.radius, // in meters
                    name: tower.name,
                });

                this.markers.push(marker, circle);
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.towers !== this.props.towers) {
            this.updateMarkers();
        }
    }

    render() {
        const { t, globalClasses, classes, center, zoom, getTowersError, towers, isLoadingControlTowers } = this.props;

        return (
            <App
                needRights={AccountRight.DRIVER_PREBOOK}
                loading={!towers}
                loadingError={this.props.getTowersError}
                onRetry={this.load}
                header={{
                    title: t('title'),
                    button: (
                        <ButtonTo
                            rights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}
                            to={paths.controlTowersAdd}
                            startIcon={<Icon>add</Icon>}
                            text={t('common:add')}
                            variante="rectangle"
                        />
                    ),
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <ErrorMessage payload={getTowersError} />
                    {isLoadingControlTowers && <MainLoading />}
                    {!isLoadingControlTowers && towers && (
                        <>
                            {towers.length === 0 ? (
                                <p>
                                    <b>{t('no_towers')}</b>
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flex: 1 }}>
                                    <div
                                        className={globalClasses.block}
                                        style={{ flex: 1, marginRight: 4, padding: '20px 28px' }}
                                    >
                                        <Listing
                                            datas={towers.map((tower) => (
                                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                    <b style={{ flex: 1 }}>{tower.name}</b>
                                                    <TooltipButton title={t('center_map')}>
                                                        <Button
                                                            size="small"
                                                            style={{ marginLeft: 8 }}
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={this.handleCenterMap(tower)}
                                                            onMouseEnter={() => {
                                                                this.selectedMaker = tower;
                                                                this.updateSelectedMarker();
                                                            }}
                                                            onMouseLeave={() => {
                                                                this.selectedMaker = null;
                                                                this.updateSelectedMarker();
                                                            }}
                                                            className={globalClasses.btnAutoWidth}
                                                        >
                                                            <Icon fontSize="small">location_on</Icon>
                                                        </Button>
                                                    </TooltipButton>
                                                    {this.isDriverPrebookWrite && (
                                                        <TooltipButton title={t('common:edit')}>
                                                            <Button
                                                                size="small"
                                                                style={{ marginLeft: 8 }}
                                                                variant="contained"
                                                                color="secondary"
                                                                component="a"
                                                                href={`${paths.controlTowersAdd}?id=${tower.id}`}
                                                                className={globalClasses.btnAutoWidth}
                                                            >
                                                                <Icon fontSize="small">edit</Icon>
                                                            </Button>
                                                        </TooltipButton>
                                                    )}
                                                </div>
                                            ))}
                                        />
                                    </div>
                                    <div
                                        className={globalClasses.blockMargin}
                                        style={{ height: '80vh', width: '100%', flex: 1, overflow: 'hidden' }}
                                    >
                                        <div className={classes.mapWrapper} ref={this.mapWrapper}>
                                            <GoogleMapReact
                                                bootstrapURLKeys={{ key: envConfig.google_key }}
                                                yesIWantToUseGoogleMapApiInternals
                                                onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                                                defaultCenter={center}
                                                defaultZoom={zoom}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </App>
        );
    }
}

const ControlTowersListWithTranslation = withGlobalStyles(
    withStyles(styles)(withTranslation(['towers', 'common'])(ControlTowersList)),
);

const mapStateToProps = (state) => ({
    isLoadingControlTowers: pendingSelectors.isLoadingControlTowers(state),
    towers: towersSelectors.towers(state),
    getTowersError: errorSelectors.getControlTowerError(state),
});

const mapDispatchToProps = {
    clearError: errorActions.clearError,
    getTowers: towersActions.getTowers,
};

export default connect(mapStateToProps, mapDispatchToProps)(ControlTowersListWithTranslation);
